/**
 * AccessControl Component
 *
 * Wrapper that conditionally renders children based on module permissions.
 * Used to protect routes and UI sections.
 *
 * Props:
 *   moduleCode  - Module code to check (e.g. 'USERS', 'BRANCHES')
 *   alternativeModuleCodes - Alternative module codes to check (can be array or single string)
 *   requireAccess - Permission type: 'read' | 'create' | 'update' | 'delete' (default: 'read')
 *   fallback    - Element to render when access denied (default: null)
 *   children    - Content to render when access is granted
 *
 * Usage:
 *   <AccessControl moduleCode="USERS" fallback={<Navigate to="/dashboard" />}>
 *     <UsersPage />
 *   </AccessControl>
 *
 *   <AccessControl moduleCode="PRODUCTS" alternativeModuleCodes={['ORDERS']}>
 *     <OrderManagementPage />
 *   </AccessControl>
 */

import { useAccessControl } from '@/hooks/useAccessControl';

const AccessControl = ({ moduleCode, alternativeModuleCodes, requireAccess = 'read', fallback = null, children }) => {
  const { hasModuleAccess, canCreate, canRead, canUpdate, canDelete } = useAccessControl();

  // If no moduleCode, always grant access
  if (!moduleCode) {
    return children;
  }

  // Helper function to check access for a specific module
  const checkModuleAccess = (code) => {
    // First check if module is enabled
    if (!hasModuleAccess(code)) {
      return false;
    }

    // Then check specific CRUD permission
    const accessChecks = {
      create: canCreate,
      read: canRead,
      update: canUpdate,
      delete: canDelete,
    };

    const checkFn = accessChecks[requireAccess];
    if (checkFn && !checkFn(code)) {
      return false;
    }

    return true;
  };

  // Check primary module
  let hasAccess = checkModuleAccess(moduleCode);

  // If primary module doesn't have access, check alternative modules
  if (!hasAccess && alternativeModuleCodes) {
    const alternatives = Array.isArray(alternativeModuleCodes)
      ? alternativeModuleCodes
      : [alternativeModuleCodes];

    hasAccess = alternatives.some(code => checkModuleAccess(code));
  }

  return hasAccess ? children : fallback;
};

export default AccessControl;
